// Abertura do GPS Drive (2026-10-06): um Porsche entra correndo pela
// esquerda, freia no meio da tela (mergulha o nariz, luz de freio acende),
// dá dois "sinais de luz" com o farol e a tela abre (fade) mostrando o
// sistema por baixo.
//
// - Toca uma vez por carregamento da página (F5/abrir app), nas rotas da
//   empresa (login e app). Não toca em navegação interna nem nas rotas
//   públicas/Super Master (montado só dentro do AuthProvider em __root).
// - Vem no HTML do SSR, então a chegada começa antes do JS carregar. O
//   "sinal de luz" só acontece depois que a chegada terminou E a sessão
//   resolveu (`ready`); se a sessão demorar, o carro fica parado com o
//   farol aceso e no máximo IDLE_MAX_MS depois abre mesmo assim (o
//   GpsBootLoader de baixo assume).
// - Imagens: render 3D único do Porsche 911 (scripts/render-loader-porsche,
//   modelo CC BY-SA 4.0 — ver models-3d/porsche/LICENSE.txt). ~40 KB.
// - Toca mesmo com prefers-reduced-motion (Windows com "efeitos de
//   animação" desligado) — pedido explícito do dono; 2026-10-06 não
//   aparecia no web por causa disso.
import { useEffect, useRef, useState } from "react";
import bodyUrl from "@/assets/loader/porsche-body.webp";
import wheelUrl from "@/assets/loader/porsche-wheel.webp";
import meta from "@/assets/loader/porsche.json";

// Farol e lanterna, em fração da imagem da carroceria (medidos no render).
const HEAD = { x: 0.875, y: 0.49 };
const TAIL = { x: 0.045, y: 0.47 };
// Base das rodas em relação ao topo da carroceria (as rodas passam da base
// da imagem, que é recortada sem elas).
const WHEEL_BOTTOM = meta.wheels[0].y + (meta.diameter * meta.aspect) / 2;

const FLASH_MS = 800;
const EXIT_MS = 520;
const IDLE_MAX_MS = 3000;

type Phase = "arrive" | "idle" | "flash" | "exit" | "done";

export function BootIntro({ ready }: { ready: boolean }) {
  const [phase, setPhase] = useState<Phase>("arrive");
  const carRef = useRef<HTMLDivElement>(null);

  // Chegada: espera a animação CSS terminar (pode já ter terminado antes da
  // hidratação — aí `finished` resolve na hora).
  useEffect(() => {
    const el = carRef.current;
    const anims = el?.getAnimations?.() ?? [];
    let alive = true;
    Promise.all(anims.map((a) => a.finished))
      .catch(() => undefined)
      .then(() => alive && setPhase((p) => (p === "arrive" ? "idle" : p)));
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (phase === "idle") {
      if (ready) {
        setPhase("flash");
        return;
      }
      const t = setTimeout(() => setPhase("flash"), IDLE_MAX_MS);
      return () => clearTimeout(t);
    }
    if (phase === "flash") {
      const t = setTimeout(() => setPhase("exit"), FLASH_MS);
      return () => clearTimeout(t);
    }
    if (phase === "exit") {
      const t = setTimeout(() => setPhase("done"), EXIT_MS);
      return () => clearTimeout(t);
    }
  }, [phase, ready]);

  if (phase === "done") return null;

  return (
    <div className="bi-root" data-phase={phase} aria-hidden>
      <div
        className="bi-stage"
        style={{ ["--bi-ar" as string]: meta.aspect, ["--bi-wb" as string]: WHEEL_BOTTOM }}
      >
        <div className="bi-ground">
          <div className="bi-lane" />
        </div>
        <div ref={carRef} className="bi-car">
          <div className="bi-streaks" />
          <div className="bi-pitch">
            <div className="bi-shadow" />
            <span className="bi-beam" style={{ left: `${HEAD.x * 100}%`, top: `${HEAD.y * 100}%` }} />
            <img src={bodyUrl} alt="" draggable={false} decoding="async" className="bi-body" />
            {meta.wheels.map((w, i) => (
              <div
                key={i}
                className="bi-wheel"
                style={{ left: `${w.x * 100}%`, top: `${w.y * 100}%`, width: `${meta.diameter * 100}%` }}
              >
                <img src={wheelUrl} alt="" draggable={false} decoding="async" />
              </div>
            ))}
            <span className="bi-flare" style={{ left: `${HEAD.x * 100}%`, top: `${HEAD.y * 100}%` }} />
            <span className="bi-tail" style={{ left: `${TAIL.x * 100}%`, top: `${TAIL.y * 100}%` }} />
          </div>
        </div>
      </div>
    </div>
  );
}
